import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";

import { apiKeyAdapter } from "@/adapters/APIKeyAdapter";
import { API_PATH } from "@/constants/config";
import { request } from "@/helper/http";
import { invalidatesOn } from "@/helper/invalidatesOn";
import Logger from "@/helper/logger";
import { type APIKey, type APIKeyCreate } from "@/types/apiKey/client";
import { type APIKeyServer } from "@/types/apiKey/server";
import { type IErrorResponse } from "@/types/error";
import { type APIQuery, type APIResponse } from "@/types/global";

const getAPIKey = (params: APIQuery): Promise<APIResponse<APIKeyServer>> => {
  return request({
    method: "GET",
    url: `${API_PATH.SYSTEM_SETTINGS_API_KEYS}`,
    params,
  });
};

const createAPIKey = (payload: unknown): Promise<APIKeyServer> => {
  return request({
    method: "POST",
    url: `${API_PATH.SYSTEM_SETTINGS_API_KEYS}`,
    data: payload,
  });
};

const rotateAPIKey = (id: string): Promise<APIKeyServer> => {
  return request({
    method: "POST",
    url: `${API_PATH.SYSTEM_SETTINGS_API_KEYS_ROTATE(id)}`,
  });
};

const deleteAPIKey = (id: string): Promise<APIKeyServer> => {
  return request({
    method: "DELETE",
    url: API_PATH.SYSTEM_SETTINGS_API_KEYS_DELETE(id),
  });
};

export const apiKeyApi = createApi({
  reducerPath: "apiKeyApi",
  baseQuery: fakeBaseQuery(),
  tagTypes: ["APIKey"],
  endpoints: (builder) => ({
    getAPIKeys: builder.query({
      queryFn: async (params: APIQuery) => {
        try {
          const response = await getAPIKey(params);
          return {
            data: {
              meta: {
                count: response.count,
                previous: response.previous,
                next: response.next,
              },
              data: apiKeyAdapter.fromBackendArray(response.results).data,
              warnings: [],
              errors: [],
              isValid: true,
            },
          };
        } catch (error) {
          Logger.error("[APIKeyService] getAPIKeys error:", {
            context: "APIKeyService",
            data: error,
          });
          return { error: error as IErrorResponse };
        }
      },
      providesTags: ["APIKey"],
    }),

    createAPIKey: builder.mutation<APIKey, APIKeyCreate>({
      queryFn: async (payload: APIKeyCreate) => {
        try {
          // Validate and convert payload using adapter
          const payloadToBackend = apiKeyAdapter.toBackend(payload);
          const rawData = await createAPIKey(payloadToBackend);
          const adapterResult = apiKeyAdapter.fromBackend(rawData);

          return { data: adapterResult.data };
        } catch (error) {
          Logger.error("[APIKeyService] createAPIKey error:", {
            context: "APIKeyService",
            data: error,
          });
          return { error: error as IErrorResponse };
        }
      },
      invalidatesTags: invalidatesOn({ success: ["APIKey"], error: [] }),
    }),

    rotateAPIKey: builder.mutation({
      queryFn: async (id: string) => {
        try {
          const rawData = await rotateAPIKey(id);

          const adapterResult = apiKeyAdapter.fromBackend(rawData);

          return { data: adapterResult.data };
        } catch (error) {
          Logger.error("[APIKeyService] rotateAPIKey error:", {
            context: "APIKeyService",
            data: error,
          });
          return { error: error as IErrorResponse };
        }
      },
      invalidatesTags: invalidatesOn({ success: ["APIKey"], error: [] }),
    }),
    deleteAPIKey: builder.mutation({
      queryFn: async (id: string) => {
        try {
          const rawData = await deleteAPIKey(id);

          const adapterResult = apiKeyAdapter.fromBackend(rawData);

          return { data: adapterResult.data };
        } catch (error) {
          Logger.error("[APIKeyService] deleteAPIKey error:", {
            context: "APIKeyService",
            data: error,
          });
          return { error: error as IErrorResponse };
        }
      },
      invalidatesTags: invalidatesOn({ success: ["APIKey"], error: [] }),
    }),
  }),
});

export const { useGetAPIKeysQuery, useCreateAPIKeyMutation, useRotateAPIKeyMutation, useDeleteAPIKeyMutation } =
  apiKeyApi;
